import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';

// Interactive color wheel for the "Reading the Color Wheel" post. The reader sets a
// hue two ways that stay in sync: drag (or click) around the ring, or drag the hue
// slider beneath it. Either one moves the other, and the centre shows the colour and
// its angle (0 to 360 degrees). It makes the post's main idea tangible: hue is
// literally a position on a circle, the same value laid out flat on the slider. The
// post marks a mount point with <!--WHEEL-START-->...<!--WHEEL-END--> around a static
// fallback; BlogPostPageComponent swaps this live component in.
@Component( {
  selector: 'app-color-wheel',
  templateUrl: './color-wheel.component.html',
  styleUrls: [ './color-wheel.component.scss' ],
} )
export class ColorWheelComponent {

  @Input() heading = 'Find A Hue On The Wheel';

  @ViewChild( 'ring' ) ringRef!: ElementRef<HTMLElement>;
  @ViewChild( 'slider' ) sliderRef!: ElementRef<HTMLElement>;

  // Hue 0..359, the angle around the wheel. Random per mount so it opens differently,
  // unless the post passes a color via [hex] (then it opens on that color's hue).
  hue = Math.floor( Math.random() * 360 );

  // Optional starting color from the post ([hex]="post.relatedColor"): the wheel opens
  // on that color's hue instead of a random one. Ignored when no valid hex is bound.
  @Input() set hex( value: string ) {
    const hsl = this.hexToHsl( value );
    if ( hsl ) {
      this.hue = Math.round( hsl.h ) % 360;
    }
  }

  private wheelDrag = false;
  private sliderDrag = false;
  private tracked = false;

  constructor( private analyticsService: AnalyticsService ) {
  }

  // Saturation / lightness of the base colour. Hue-only mode keeps the fixed vivid
  // pair; in harmony mode the colour picker can change them, and the wheel only
  // rotates the hue, so the whole scheme stays in the user's chosen colour family.
  baseSat = 90;
  baseLight = 55;

  // The base colour: the current hue at the chosen saturation / lightness.
  get hex(): string {
    return this.hslToHex( this.hue, this.baseSat, this.baseLight );
  }

  get name(): string {
    return this.nameOf( this.hue );
  }

  // ---- harmony mode: opposites geometry on the wheel --------------------------
  // When `harmony` is set the wheel is a read-only diagram (no picker / no slider).
  // It is given the EXACT scheme colours via [colors] (base first, then partners),
  // so its dots match the swatches beside it perfectly. Each colour is placed on
  // the rim by its own hue, with a line from the base to each partner. Used by the
  // "Harmony in Opposites" post.
  @Input() harmony = false;
  @Input() colors: string[] = [];
  // The base (source) colour of the scheme. Lines fan out from this one. It is not
  // always colors[0] (for analogous it sits in the middle), so it is matched by hex.
  @Input() base = '';
  // Connector geometry: 'fan' draws a line from the base to each partner (the
  // default, for complementary / split / analogous); 'polygon' orders the colours
  // around the wheel and joins each to the next, closing the loop into a filled-out
  // shape — a triangle for triadic, a rectangle for tetradic.
  @Input() shape: 'fan' | 'polygon' = 'fan';
  // Temperature mode: instead of connector lines, split the wheel with one long
  // horizontal line into a warm top half and a cool bottom half (warm hues sit at
  // 270..360..90, cool at 90..270). Used by the color tool's Temperature row.
  @Input() divide = false;

  // Temperature widget (interactive, for the "Warm and Cool Colors" post): the
  // live hue-only wheel, but split warm-top / cool-bottom with the same divider +
  // Warm/Cool labels as the color tool, and the readout below names the half the
  // picker is in ("Warm" / "Cool") instead of the hex.
  @Input() temperature = false;

  // Warm hues sit on the top half of the ring (270..360..90), cool on the bottom
  // (90..270). The boundaries (90, 270) read as warm.
  get isWarm(): boolean {
    return this.hue <= 90 || this.hue >= 270;
  }

  get temperatureWord(): string {
    return this.isWarm ? 'Warm' : 'Cool';
  }

  // Rim points (208-unit viewBox, centre 104, radius 84): each scheme colour placed
  // at its own hue, carrying the exact colour for the SVG dot.
  get schemePoints(): { x: number; y: number; hex: string }[] {
    const C = 104, R = 84;
    return ( this.colors || [] ).map( hex => {
      const hsl = this.hexToHsl( hex ) || { h: 0, s: 0, l: 0 };
      const a = hsl.h * Math.PI / 180;
      return {
        x: +( C + R * Math.sin( a ) ).toFixed( 1 ),
        y: +( C - R * Math.cos( a ) ).toFixed( 1 ),
        hex,
      };
    } );
  }

  // The index of the base colour within the scheme (matched by hex). Falls back to
  // the first colour when no base is supplied / matched.
  get baseIndex(): number {
    const b = ( this.base || '' ).replace( /^#/, '' ).toLowerCase();
    if ( b ) {
      const i = ( this.colors || [] ).findIndex( c => ( c || '' ).replace( /^#/, '' ).toLowerCase() === b );
      if ( i >= 0 ) {
        return i;
      }
    }
    return 0;
  }

  get basePoint(): { x: number; y: number; hex: string } {
    return this.schemePoints[ this.baseIndex ] || { x: 104, y: 104, hex: '#000000' };
  }

  // Draw order for the dots: the base colour LAST so it sits on top. It only matters
  // when dots overlap (monochrome, where every colour shares the base's hue and the
  // dots stack), keeping the user's own colour visible above the stack.
  get dotPoints(): { x: number; y: number; hex: string }[] {
    const pts = this.schemePoints;
    const bi = this.baseIndex;
    if ( bi < 0 || bi >= pts.length ) {
      return pts;
    }
    return [ ...pts.slice( 0, bi ), ...pts.slice( bi + 1 ), pts[ bi ] ];
  }

  // The connector lines. 'polygon': order the points by their angle around the
  // wheel and join each to the next (closing the loop), so every colour links to
  // its two neighbours. 'fan' (default): a line from the base to each partner; the
  // base can sit anywhere in the list (the middle, for analogous).
  get geoLines(): { x1: number; y1: number; x2: number; y2: number }[] {
    const pts = this.schemePoints;
    if ( this.shape === 'polygon' && pts.length > 2 ) {
      const C = 104;
      const ring = [ ...pts ].sort(
        ( a, b ) => Math.atan2( a.y - C, a.x - C ) - Math.atan2( b.y - C, b.x - C ),
      );
      return ring.map( ( p, i ) => {
        const q = ring[ ( i + 1 ) % ring.length ];
        return { x1: p.x, y1: p.y, x2: q.x, y2: q.y };
      } );
    }
    const b = this.basePoint;
    const bi = this.baseIndex;
    return pts
      .filter( ( _p, i ) => i !== bi )
      .map( p => ( { x1: b.x, y1: b.y, x2: p.x, y2: p.y } ) );
  }

  // Each scheme colour with its hue angle, listed beside the wheel.
  get schemeLabels(): { hex: string; hue: number }[] {
    return ( this.colors || [] ).map( hex => {
      const hsl = this.hexToHsl( hex );
      return { hex, hue: Math.round( hsl ? hsl.h : 0 ) };
    } );
  }

  // The hue ring rendered at the BASE colour's saturation / lightness (not a fixed
  // vivid pair), so a muted base like #aca586 gives a muted ring of every hue.
  get ringGradient(): string {
    // Use the BASE colour's saturation / lightness, not colors[0]. For monochrome the
    // first entry is the darkest step, which would render a dark, muted ring; the base
    // keeps the ring open and vivid, matching the analogous / triadic wheels.
    const baseHex = ( this.colors && this.colors.length ) ? this.colors[ this.baseIndex ] : null;
    const hsl = baseHex ? this.hexToHsl( baseHex ) : null;
    const s = hsl ? Math.round( hsl.s ) : 90;
    const l = hsl ? Math.round( hsl.l ) : 55;
    const stops = [ 0, 60, 120, 180, 240, 300, 360 ].map( h => `hsl(${ h }, ${ s }%, ${ l }%)` ).join( ', ' );
    return `conic-gradient(from 0deg, ${ stops })`;
  }

  private hexToHsl( hex: string ): { h: number; s: number; l: number } | null {
    const m = /^#?([0-9a-f]{6})$/i.exec( ( hex || '' ).trim() );
    if ( !m ) {
      return null;
    }
    const n = parseInt( m[ 1 ], 16 );
    const r = ( ( n >> 16 ) & 255 ) / 255, g = ( ( n >> 8 ) & 255 ) / 255, b = ( n & 255 ) / 255;
    const max = Math.max( r, g, b ), min = Math.min( r, g, b );
    const l = ( max + min ) / 2;
    let h = 0, s = 0;
    if ( max !== min ) {
      const d = max - min;
      s = l > 0.5 ? d / ( 2 - max - min ) : d / ( max + min );
      if ( max === r ) { h = ( ( g - b ) / d + ( g < b ? 6 : 0 ) ); }
      else if ( max === g ) { h = ( b - r ) / d + 2; }
      else { h = ( r - g ) / d + 4; }
      h *= 60;
    }
    return { h, s: s * 100, l: l * 100 };
  }

  // The centre angle label flips to dark on light hues (yellow, lime) and white on
  // dark hues, so it stays readable all the way around the wheel.
  get angleDark(): boolean {
    const h = this.hex.replace( '#', '' );
    const r = parseInt( h.slice( 0, 2 ), 16 );
    const g = parseInt( h.slice( 2, 4 ), 16 );
    const b = parseInt( h.slice( 4, 6 ), 16 );
    return ( 0.299 * r + 0.587 * g + 0.114 * b ) > 150;
  }

  // ---- the wheel (drag or click anywhere on the ring) -------------------------

  onWheelDown( ev: PointerEvent ): void {
    this.wheelDrag = true;
    this.capture( this.ringRef, ev );
    this.setFromWheel( ev );
    this.track();
  }

  onWheelMove( ev: PointerEvent ): void {
    if ( this.wheelDrag ) {
      this.setFromWheel( ev );
    }
  }

  onWheelUp(): void {
    this.wheelDrag = false;
  }

  // Hue = the angle from the wheel's centre to the pointer, measured from the top
  // (12 o'clock = 0 = red) going clockwise, matching the conic-gradient ring.
  private setFromWheel( ev: PointerEvent ): void {
    const r = this.ringRef.nativeElement.getBoundingClientRect();
    const dx = ev.clientX - ( r.left + r.width / 2 );
    const dy = ev.clientY - ( r.top + r.height / 2 );
    let a = Math.atan2( dx, -dy ) * 180 / Math.PI;
    if ( a < 0 ) {
      a += 360;
    }
    this.hue = Math.round( a ) % 360;
  }

  // ---- the hue slider (the same hue, laid out flat) ---------------------------

  onSliderDown( ev: PointerEvent ): void {
    this.sliderDrag = true;
    this.capture( this.sliderRef, ev );
    this.setFromSlider( ev );
    this.track();
  }

  onSliderMove( ev: PointerEvent ): void {
    if ( this.sliderDrag ) {
      this.setFromSlider( ev );
    }
  }

  onSliderUp(): void {
    this.sliderDrag = false;
  }

  private setFromSlider( ev: PointerEvent ): void {
    const r = this.sliderRef.nativeElement.getBoundingClientRect();
    const t = Math.max( 0, Math.min( 1, ( ev.clientX - r.left ) / r.width ) );
    this.hue = Math.round( t * 360 ) % 360;
  }

  // ---- helpers ----------------------------------------------------------------

  private capture( ref: ElementRef<HTMLElement>, ev: PointerEvent ): void {
    // Keep move events even if the pointer leaves the control. Guarded: a stray
    // pointer id would otherwise throw.
    try {
      ref.nativeElement.setPointerCapture( ev.pointerId );
    } catch {
      // ignore: no active pointer with this id
    }
  }

  private track(): void {
    if ( this.tracked ) {
      return;
    }
    this.tracked = true;
    this.analyticsService.logEvent( 'Blog Widget Used', { widget: 'color-wheel' } );
  }

  private hslToHex( h: number, s: number, l: number ): string {
    s /= 100;
    l /= 100;
    const c = ( 1 - Math.abs( 2 * l - 1 ) ) * s;
    const x = c * ( 1 - Math.abs( ( ( h / 60 ) % 2 ) - 1 ) );
    const m = l - c / 2;
    let r = 0, g = 0, b = 0;
    if ( h < 60 ) { r = c; g = x; }
    else if ( h < 120 ) { r = x; g = c; }
    else if ( h < 180 ) { g = c; b = x; }
    else if ( h < 240 ) { g = x; b = c; }
    else if ( h < 300 ) { r = x; b = c; }
    else { r = c; b = x; }
    const to = ( v: number ) => Math.round( ( v + m ) * 255 ).toString( 16 ).padStart( 2, '0' );
    return ( '#' + to( r ) + to( g ) + to( b ) ).toUpperCase();
  }

  private nameOf( h: number ): string {
    const names: [ number, string ][] = [
      [ 15, 'red' ], [ 45, 'orange' ], [ 70, 'yellow' ], [ 160, 'green' ],
      [ 200, 'cyan' ], [ 255, 'blue' ], [ 290, 'violet' ], [ 330, 'magenta' ], [ 360, 'red' ],
    ];
    for ( const [ edge, nm ] of names ) {
      if ( h <= edge ) {
        return nm;
      }
    }
    return 'red';
  }
}
